import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import {AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {PlantRiskModel} from '../_models/plant-risk.model';
import {PlantRiskService} from '../_services/plant-risk.service';
import {Subject} from 'rxjs';
import {AmchartsService} from '../../shared/_services/amcharts.service';
import {takeUntil} from 'rxjs/operators';
import {AssetCatAssetModel, AssetCustomerModel} from '../_models/asset.model';
import {CssService} from '../_services/css.service';
import {CssModel} from '../_models/css.model';
import {DfService} from '../../shared/_services/df.service';

@Component({
  selector: 'app-asset-chart',
  templateUrl: './asset-chart.component.html',
  styleUrls: ['./asset-chart.component.scss']
})

export class AssetChartComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() cust: AssetCustomerModel;
  @Input() asset: AssetCatAssetModel;

  @ViewChild('asset_chartdiv') chartDiv: ElementRef;

  private pr: PlantRiskModel;
  private css: CssModel;

  private chart: am4charts.XYChart;

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private amChartService: AmchartsService,
    private cssService: CssService,
    private plantRiskService: PlantRiskService,
    private ngZone: NgZone,
  ) {
  }

  ngOnInit() {
    this.getIm();
    this.getCss();
  }

  ngAfterViewInit() {
    this.updateChartData();
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
    this.onDestroy$.next();
  }

  // ********************************************************************************************************
  // LOAD DATA
  // ********************************************************************************************************

  private getIm(): void {
    this.plantRiskService.onNewPlantRisk()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (im) => {
          if (im.length) {
            this.pr = im.find((i) => i.asset === this.asset.id) || {asset: '', assetLabel: '', country: '', prData: []};
            this.updateChartData();
          }
        }
      );
  }

  private getCss(): void {
    this.cssService.onNewCss()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (css) => {
          if (css.length) {
            this.css = css.find((i) => i.asset === this.asset.id) || {asset: '', assetLabel: '', country: '', cssData: []};
            this.updateChartData();
          }
        }
      );
  }

  // ********************************************************************************************************
  // PROCESS DATA
  // ********************************************************************************************************

  private updateChartData(): void {
    this.createChart();
    if (this.chart && this.pr && this.css) {
      const merged = DfService.dfMerge([this.pr.prData, this.css.cssData], 'outer', 'ts');
      this.ngZone.runOutsideAngular(() => {
        this.chart.data = merged;
      });
    }
  }

  // ********************************************************************************************************
  // UI
  // ********************************************************************************************************

  private createChart(): void {
    if (!this.chart && this.chartDiv?.nativeElement) {
      this.ngZone.runOutsideAngular(() => {
        const chart = am4core.create('asset-chart__chart_' + this.asset.id, am4charts.XYChart);
        this.amChartService.setChartDefaults(chart, true, this.cust.label + ' - ' + this.asset.label);
        chart.dateFormatter.inputDateFormat = 'yyyy-MM-dd HH:mm';
        // chart.dateFormatter.timezone = 'CET';

        // AXES
        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        this.amChartService.setDateAxis(dateAxis, 'date', true, false, true, 80);
        dateAxis.tooltipDateFormat = 'yyyy-MM-dd HH:mm';
        dateAxis.dateFormats.setKey('day', 'MMM dd \'(\'EEE\')\'');
        const valueAxis1 = chart.yAxes.push(new am4charts.ValueAxis());
        this.amChartService.setValueAxis(valueAxis1, 'MW');
        valueAxis1.min = 0; // force axis minimum to zero
        const valueAxis2 = chart.yAxes.push(new am4charts.ValueAxis());
        this.amChartService.setValueAxis(valueAxis2, '€/MWh');
        valueAxis2.renderer.opposite = true;
        valueAxis2.syncWithAxis = valueAxis1;

        // SERIES
        const seriesCss = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesCss,
          'date',
          this.asset.cat === 'Coal' ? 'cds' : 'css kprol pmax',
          'ts',
          'css',
          null,
          this.amChartService.GREY,
        );
        seriesCss.yAxis = valueAxis2;
        seriesCss.xAxis = dateAxis;
        seriesCss.fillOpacity = 0.4;
        seriesCss.strokeWidth = 0;

        const seriesPmax = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesPmax,
          'date',
          'pmax available',
          'ts',
          'pmax',
          null,
          this.amChartService.GREEN_LIGHT,
        );
        seriesPmax.yAxis = valueAxis1;
        seriesPmax.xAxis = dateAxis;

        const seriesPsched = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesPsched,
          'date',
          'production plan',
          'ts',
          'psched',
          null,
          this.amChartService.PRIMARY,
        );
        seriesPsched.yAxis = valueAxis1;
        seriesPsched.xAxis = dateAxis;

        this.chart = chart;

      });
    }
  }

}
